import { ws as coreWs, initSwitchCore, WebSocketEventType } from "@/core_bridge/module_api";
import { appConfig } from "@/app/module_api";
import { useStore } from "@/state/legacy_module_api";
import { performClientLogout } from "@/state/logout";
import { EventBoundaryPolice } from "./event_boundary_police";

// Simple WS service wrapping switch-core ws
// Allows registering event handlers; idempotent connect by url/token.

export type WsEvent = { type: WebSocketEventType | string; payload?: Record<string, unknown> };
export type WsHandlers = {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onMessage?: (evt: WsEvent) => void;
  onError?: (err: Error) => void;
  onRaw?: (evt: WsEvent) => void;
};
export type WebSocketHandlers = WsHandlers;

let initialized = false;
let lastUrl: string | undefined;
let lastToken: string | undefined;
let handlers: WsHandlers = {};
let connectStarted = false;
let connectPromise: Promise<void> | null = null;
let activeEventHandler: ((eventJson: string) => void) | null = null;
let pendingOutboundMessages: Array<{ type: WebSocketEventType | string; payload: Record<string, unknown> }> = [];
const maxPendingOutboundMessages = 100;

const noopEventHandler = () => {};

function detachEventHandler() {
  if (!activeEventHandler) return;
  try {
    coreWs.onEvent(noopEventHandler);
  } catch (err) {
    console.warn("[ws-service] failed to detach event handler", err);
  }
  activeEventHandler = null;
}

function ensureEventHandler() {
  if (activeEventHandler) return;
  activeEventHandler = (eventJson: string) => {
    try {
      EventBoundaryPolice.assertInboundWebSocket(eventJson);
      const evt = JSON.parse(eventJson) as WsEvent;
      if (
        evt.type === WebSocketEventType.MESSAGE_RECEIVED ||
        evt.type === WebSocketEventType.SYSTEM_NOTIFICATION ||
        evt.type === WebSocketEventType.MESSAGE_SENT ||
        evt.type === WebSocketEventType.CONNECTION ||
        evt.type === WebSocketEventType.CONNECTION_OPEN ||
        evt.type === WebSocketEventType.DISCONNECTED ||
        evt.type === WebSocketEventType.DISCONNECT
      ) {
        console.info("[ws-service] raw event", {
          type: evt.type,
          payloadKeys: evt.payload ? Object.keys(evt.payload) : [],
          messageId: (evt.payload as any)?.id || (evt.payload as any)?.messageId || null,
        });
      }
      handlers.onRaw?.(evt);
      if (evt.type === WebSocketEventType.SESSION_REVOKED) {
        connectStarted = false;
        connectPromise = null;
        void performClientLogout("session_revoked");
        return;
      }
      switch (evt.type) {
        case WebSocketEventType.CONNECTION:
        case WebSocketEventType.CONNECTION_OPEN:
          handlers.onConnect?.();
          void flushPendingOutboundMessages();
          break;
        case WebSocketEventType.DISCONNECTED:
        case WebSocketEventType.DISCONNECT:
          connectStarted = false;
          connectPromise = null;
          handlers.onDisconnect?.();
          break;
        default:
          handlers.onMessage?.(evt);
          break;
      }
    } catch (err) {
      console.error("[ws-service] event parse failed", err);
      handlers.onError?.(err as Error);
    }
  };
  coreWs.onEvent(activeEventHandler);
}

export async function configure(token: string, url = appConfig.websocketUrl) {
  await initSwitchCore();
  console.info("[ws-service] configure", {
    url,
    initialized,
    sameUrl: lastUrl === url,
    sameToken: lastToken === token,
    tokenPrefix: token.slice(0, 12),
  });
  if (lastUrl === url && lastToken === token && initialized) return;
  if (initialized && coreWs.destroy) {
    detachEventHandler();
    try {
      coreWs.destroy();
    } catch (err) {
      console.warn("[ws-service] destroy failed", err);
    }
  }
  coreWs.create(url, token);
  initialized = true;
  lastUrl = url;
  lastToken = token;
  connectStarted = false;
  connectPromise = null;
}

function enqueuePendingOutboundMessage(
  type: WebSocketEventType | string,
  payload: Record<string, unknown>,
  reason: string
) {
  if (pendingOutboundMessages.length >= maxPendingOutboundMessages) {
    console.error("[ws-service] outbound queue full, dropping message", { reason, type });
    return;
  }
  pendingOutboundMessages.push({ type, payload });
  console.info("[ws-service] queued outbound message", {
    reason,
    type,
    queued: pendingOutboundMessages.length,
    messageId: (payload as any)?.id || null,
  });
}

async function sendNow(type: WebSocketEventType | string, payload: Record<string, unknown>) {
  const messageStr = JSON.stringify({ type, payload });
  EventBoundaryPolice.assertOutboundWebSocket(messageStr);
  await coreWs.sendRaw(messageStr);
}

async function flushPendingOutboundMessages() {
  if (pendingOutboundMessages.length === 0) return;
  if (!initialized || !isConnected()) return;

  const queued = pendingOutboundMessages;
  pendingOutboundMessages = [];
  console.info("[ws-service] flushing queued outbound messages", { count: queued.length });

  for (const message of queued) {
    try {
      await sendNow(message.type, message.payload);
    } catch (err) {
      console.warn("[ws-service] queued outbound send failed", err);
      enqueuePendingOutboundMessage(message.type, message.payload, "send_failed");
      break;
    }
  }
}

export function setHandlers(h: WsHandlers) {
  handlers = { ...handlers, ...h };
}

export async function connect(token?: string, url = appConfig.websocketUrl) {
  console.info("[ws-service] connect requested", {
    hasToken: !!token,
    initialized,
    connectStarted,
    url,
    tokenPrefix: token?.slice(0, 12) || null,
  });
  if (!initialized) {
    if (token) {
      await configure(token, url);
    } else {
      throw new Error("WS not configured: call configure first");
    }
  }
  if (connectStarted) {
    console.info("[ws-service] connect skipped: already started");
    return connectPromise ?? Promise.resolve();
  }
  connectStarted = true;
  ensureEventHandler();
  connectPromise = (async () => {
    try {
      console.info("[ws-service] core connect begin");
      await coreWs.connect();
      console.info("[ws-service] core connect resolved");
      await flushPendingOutboundMessages();
    } catch (err) {
      connectStarted = false;
      connectPromise = null;
      handlers.onError?.(err as Error);
      throw err;
    }
  })();
  try {
    await connectPromise;
  } catch (err) {
    throw err;
  } finally {
    connectPromise = null;
  }
}

export function disconnect() {
  console.log("[ws-service] disconnect() called");
  detachEventHandler();
  try {
    coreWs.disconnect();
  } catch (err) {
    console.warn("[ws-service] ws.disconnect failed", err);
  }
  try {
    coreWs.destroy();
  } catch (err) {
    console.warn("[ws-service] ws.destroy failed", err);
  }
  // Update store state
  useStore.getState().setWsConnected(false);
  handlers.onDisconnect?.();
  initialized = false;
  lastUrl = undefined;
  lastToken = undefined;
  connectStarted = false;
  connectPromise = null;
  console.log("[ws-service] wsConnected set to false, state reset");
}

export function isConnected() {
  try {
    return coreWs.isConnected();
  } catch {
    return false;
  }
}

export function send(type: WebSocketEventType | string, payload: Record<string, unknown>) {
  if (!initialized || !isConnected()) {
    enqueuePendingOutboundMessage(type, payload, "not_connected");
    return Promise.resolve();
  }

  return sendNow(type, payload).catch((err) => {
    enqueuePendingOutboundMessage(type, payload, "send_failed");
    throw err;
  });
}

/** Set the authentication token (canonical name, calls configure) */
export async function setToken(token: string) {
  await configure(token);
}

/** WebSocketService namespace for canonical module API export */
export const WebSocketService = {
  configure,
  setHandlers,
  connect,
  disconnect,
  isConnected,
  send,
  setToken,
};
